import type * as React from "react";
import type { ColumnDefinition } from "../../../ui/components/Table/types";
import type { SystemEvent } from "../../lib/api/system-events";
import type { TranslateValues } from "../../lib/i18n";

function Badge({
  children,
  tone = "neutral",
}: {
  children: React.ReactNode;
  tone?: "neutral" | "blue" | "green" | "amber" | "red";
}) {
  const styles: Record<string, React.CSSProperties> = {
    neutral: { background: "#F3F4F6", color: "#6B7280", border: "1px solid #E5E7EB" },
    blue: { background: "#EFF6FF", color: "#2563EB", border: "1px solid #BFDBFE" },
    green: { background: "#ECFDF5", color: "#059669", border: "1px solid #A7F3D0" },
    amber: { background: "#FFFBEB", color: "#D97706", border: "1px solid #FDE68A" },
    red: { background: "#FEF2F2", color: "#DC2626", border: "1px solid #FECACA" },
  };
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        padding: "1px 7px",
        borderRadius: 9999,
        ...styles[tone],
      }}
    >
      {children}
    </span>
  );
}

/** Domains present in the system-events catalog (user-service). */
const DOMAIN_FILTER_OPTIONS: Array<{ value: string; labelKey: string }> = [
  { value: "user", labelKey: "pro.system.events.domain.user" },
  { value: "organization", labelKey: "pro.system.events.domain.organization" },
  { value: "role", labelKey: "pro.system.events.domain.role" },
  { value: "permission", labelKey: "pro.system.events.domain.permission" },
  { value: "apiKey", labelKey: "pro.system.events.domain.apiKey" },
  { value: "localization", labelKey: "pro.system.events.domain.localization" },
  { value: "certification", labelKey: "pro.system.events.domain.certification" },
  { value: "product", labelKey: "pro.system.events.domain.product" },
  { value: "partnerApi", labelKey: "pro.system.events.domain.partnerApi" },
];

export function createSystemEventsTableColumns(
  t: (key: string, values?: TranslateValues) => string,
): ColumnDefinition<SystemEvent>[] {
  return [
    {
      id: "label",
      label: t("pro.system.events.col.label"),
      sortable: true,
      pinnedLeft: true,
      render: (row) => row.label,
    },
    {
      id: "key",
      label: t("pro.system.events.col.key"),
      sortable: true,
      render: (row) => (
        <span style={{ fontFamily: "monospace", fontSize: 13 }}>{row.key}</span>
      ),
    },
    {
      id: "domain",
      label: t("pro.system.events.col.domain"),
      sortable: true,
      filterType: "checkbox",
      filterOptions: DOMAIN_FILTER_OPTIONS.map(({ value, labelKey }) => ({
        value,
        label: t(labelKey),
      })),
      render: (row) => <Badge>{row.domain}</Badge>,
    },
    {
      id: "sourceService",
      label: t("pro.system.events.col.source"),
      sortable: true,
      filterType: "checkbox",
      filterOptions: [
        {
          value: "user-service",
          label: t("pro.system.events.source.userService"),
        },
        {
          value: "product-service",
          label: t("pro.system.events.source.productService"),
        },
      ],
      render: (row) => (
        <Badge tone="blue">
          {row.sourceService === "product-service"
            ? t("pro.system.events.source.productService")
            : t("pro.system.events.source.userService")}
        </Badge>
      ),
    },
    {
      id: "shouldLog",
      label: t("pro.system.events.col.shouldLog"),
      render: (row) =>
        row.shouldLog ? (
          <Badge tone="green">{t("pro.system.events.yes")}</Badge>
        ) : (
          <Badge tone="neutral">{t("pro.system.events.no")}</Badge>
        ),
    },
    {
      id: "enabled",
      label: t("pro.system.events.col.enabled"),
      render: (row) =>
        row.enabled ? (
          <Badge tone="green">{t("pro.system.events.enabled")}</Badge>
        ) : (
          <Badge tone="red">{t("pro.system.events.disabled")}</Badge>
        ),
    },
    {
      id: "retiredAt",
      label: t("pro.system.events.col.status"),
      render: (row) =>
        row.retiredAt ? (
          <Badge tone="amber">{t("pro.system.events.retired")}</Badge>
        ) : (
          <Badge tone="green">{t("pro.system.events.active")}</Badge>
        ),
    },
  ];
}
